import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Mail, CheckCircle2, Loader2 } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { useT } from "@/i18n/LanguageContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * NotifyMeModal — pre-launch waitlist capture for Virtue Guide.
 * Black & gold, mobile-friendly, with smooth fade/scale animation.
 *
 * Props:
 *   open: boolean
 *   source: "app_store" | "google_play"  (which CTA triggered the modal)
 *   onClose: () => void
 */
export default function NotifyMeModal({ open, source, onClose }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const inputRef = useRef(null);
  const { t } = useT();

  // Auto-focus the email input shortly after the modal mounts
  useEffect(() => {
    if (!open) return;
    const tm = setTimeout(() => inputRef.current?.focus(), 220);
    return () => clearTimeout(tm);
  }, [open]);

  // Close on Escape + lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      setStatus("error");
      setErrorMsg(t("notify.errInvalid"));
      return;
    }
    setStatus("submitting");
    setErrorMsg("");
    try {
      const res = await fetch(`${API_BASE}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, source: source || "unknown" }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setStatus("success");
      trackEvent("notify_me_submission", {
        store: source || "unknown",
        already_subscribed: !!data.already_subscribed,
      });
    } catch (err) {
      setStatus("error");
      setErrorMsg(t("notify.errGeneric"));
    }
  };

  if (!open) return null;

  const storeLabel =
    source === "app_store" ? t("notify.storeApple") :
    source === "google_play" ? t("notify.storeGoogle") :
    t("notify.storeBoth");

  const modal = (
    <div
      data-testid="notify-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="notify-modal-title"
      className="fixed inset-0 z-[100]"
      style={{ animation: "fadeIn 220ms ease-out" }}
    >
      {/* backdrop */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Close dialog"
        data-testid="notify-modal-backdrop"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClose(); } }}
        className="fixed inset-0 bg-black/80 backdrop-blur-md cursor-pointer"
      />

      {/* card wrapper */}
      <div className="relative z-10 w-full h-full flex items-center justify-center px-4 py-8 pointer-events-none">
        <div
          className="relative w-full max-w-lg bg-ink-elevated border border-gold/30 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.95)] p-1 pointer-events-auto"
          style={{ animation: "modalIn 360ms cubic-bezier(.2,.7,.2,1) both" }}
        >
        {/* gold inner glow */}
        <div className="absolute -inset-px bg-gradient-to-br from-gold/30 via-transparent to-gold/10 -z-10 blur-md opacity-50 pointer-events-none" aria-hidden />

        <div className="relative bg-ink p-8 md:p-10">
          {/* close */}
          <button
            type="button"
            aria-label="Close dialog"
            data-testid="notify-modal-close"
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-fog hover:text-gold transition-colors"
          >
            <X size={20} strokeWidth={1.5} />
          </button>

          {status !== "success" ? (
            <>
              {/* gold mark */}
              <div className="flex justify-center mb-6">
                <div className="relative w-12 h-12 flex items-center justify-center">
                  <span className="absolute inset-0 rounded-full bg-gold/15 blur-md" aria-hidden />
                  <Mail size={22} color="#C5A059" strokeWidth={1.5} className="relative" />
                </div>
              </div>

              <p className="text-center text-[11px] tracking-[0.3em] uppercase text-gold mb-4">
                {t("notify.overline")}
              </p>
              <h3
                id="notify-modal-title"
                data-testid="notify-modal-title"
                className="font-serif text-3xl md:text-4xl text-bone text-center leading-tight tracking-tight"
              >
                {t("notify.headline1")}<br className="hidden sm:inline" /> <span className="italic text-gold">{t("notify.headline2")}</span>
              </h3>

              <p className="mt-5 text-center text-fog font-light leading-relaxed max-w-sm mx-auto">
                {t("notify.body", { store: storeLabel })}
              </p>

              <form onSubmit={onSubmit} noValidate data-testid="notify-form" className="mt-8 space-y-4">
                <label className="block">
                  <span className="text-[10px] tracking-[0.25em] uppercase text-gold mb-2 block">{t("notify.emailLabel")}</span>
                  <input
                    ref={inputRef}
                    type="email"
                    required
                    data-testid="notify-input-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    inputMode="email"
                    className="w-full bg-transparent border border-white/15 focus:border-gold outline-none py-3 px-4 text-bone placeholder:text-fog/40 transition-colors"
                  />
                </label>

                {status === "error" && (
                  <p data-testid="notify-error" className="text-sm text-[#E89B6B] font-light">
                    {errorMsg}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  data-testid="notify-submit"
                  className="w-full inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold-hover text-ink px-6 py-4 text-[12px] tracking-[0.28em] uppercase font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? (
                    <><Loader2 size={16} className="animate-spin" strokeWidth={1.5} /> {t("notify.sending")}</>
                  ) : (
                    <>{t("notify.button")}</>
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-[10px] tracking-[0.22em] uppercase text-fog/60">
                {t("notify.noSpam")}
              </p>
            </>
          ) : (
            <div data-testid="notify-success" className="text-center py-4">
              <div className="flex justify-center mb-6">
                <CheckCircle2 size={42} color="#C5A059" strokeWidth={1.25} />
              </div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-3">{t("notify.successOverline")}</p>
              <h3 className="font-serif text-3xl md:text-4xl text-bone leading-tight tracking-tight">
                <span className="italic text-gold">{t("notify.thanks")}</span>
              </h3>
              <p className="mt-5 text-fog font-light leading-relaxed max-w-sm mx-auto">
                {t("notify.successBody")} <span className="text-gold">{t("notify.knowThyself")}</span>
              </p>
              <button
                type="button"
                onClick={onClose}
                data-testid="notify-success-close"
                className="mt-8 inline-flex items-center gap-2 text-[12px] tracking-[0.28em] uppercase text-gold hover:text-gold-hover transition-colors"
              >
                {t("notify.close")}
              </button>
            </div>
          )}
        </div>
      </div>
      </div>

      {/* local keyframes */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes modalIn {
          from { opacity: 0; transform: translateY(12px) scale(.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1);   }
        }
      `}</style>
    </div>
  );

  return createPortal(modal, document.body);
}
